/**
 * Convert an SVG path `d=` attribute into a sequence of pdf-lib operator
 * strings (e.g. "x y m", "x1 y1 x2 y2 x y c"). The caller pushes these into
 * a pdf-lib page op stream, followed by `clip()` + `endPath()` to use them
 * as a clip mask.
 *
 * Coord system: SVG is Y-down, PDF is Y-up. We flip Y by computing
 * `pdfY = bboxH - svgY` so the path renders the same orientation.
 *
 * The path is expected in pixel-space (run scalePath() first if you have
 * unit-space coords). bboxW / bboxH are used only for the Y-flip and for
 * elliptical-arc approximation reference.
 */
export function svgPathToPdfOps(d: string, _bboxW: number, bboxH: number): string[] {
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
  if (!tokens) return [];

  let cmd = '';
  let i = 0;
  let cx = 0, cy = 0;  // current point in PDF space (Y already flipped)
  const ops: string[] = [];
  const flipY = (y: number) => bboxH - y;

  while (i < tokens.length) {
    const t = tokens[i];
    if (/^[a-zA-Z]$/.test(t)) {
      // Handle commands that consume no numeric args (Z/z)
      if (t === 'Z' || t === 'z') {
        ops.push('h');
        i++;
        continue;
      }
      cmd = t;
      i++;
      continue;
    }
    switch (cmd) {
      case 'M': {
        const x = parseFloat(tokens[i]);
        const y = flipY(parseFloat(tokens[i + 1]));
        ops.push(`${x} ${y} m`);
        cx = x; cy = y;
        cmd = 'L';  // subsequent pairs are implicit L
        i += 2;
        break;
      }
      case 'L': {
        const x = parseFloat(tokens[i]);
        const y = flipY(parseFloat(tokens[i + 1]));
        ops.push(`${x} ${y} l`);
        cx = x; cy = y;
        i += 2;
        break;
      }
      case 'H': {
        const x = parseFloat(tokens[i]);
        ops.push(`${x} ${cy} l`);
        cx = x;
        i++;
        break;
      }
      case 'V': {
        const y = flipY(parseFloat(tokens[i]));
        ops.push(`${cx} ${y} l`);
        cy = y;
        i++;
        break;
      }
      case 'C': {
        const x1 = parseFloat(tokens[i]);
        const y1 = flipY(parseFloat(tokens[i + 1]));
        const x2 = parseFloat(tokens[i + 2]);
        const y2 = flipY(parseFloat(tokens[i + 3]));
        const x = parseFloat(tokens[i + 4]);
        const y = flipY(parseFloat(tokens[i + 5]));
        ops.push(`${x1} ${y1} ${x2} ${y2} ${x} ${y} c`);
        cx = x; cy = y;
        i += 6;
        break;
      }
      case 'Q': {
        // Convert quadratic → cubic. Standard formula:
        //   c1 = start + 2/3 * (ctrl - start)
        //   c2 = end + 2/3 * (ctrl - end)
        const qx1 = parseFloat(tokens[i]);
        const qy1 = flipY(parseFloat(tokens[i + 1]));
        const qx = parseFloat(tokens[i + 2]);
        const qy = flipY(parseFloat(tokens[i + 3]));
        const c1x = cx + (2 / 3) * (qx1 - cx);
        const c1y = cy + (2 / 3) * (qy1 - cy);
        const c2x = qx + (2 / 3) * (qx1 - qx);
        const c2y = qy + (2 / 3) * (qy1 - qy);
        ops.push(`${c1x} ${c1y} ${c2x} ${c2y} ${qx} ${qy} c`);
        cx = qx; cy = qy;
        i += 4;
        break;
      }
      case 'A': {
        // Elliptical arc → approximate with cubic Beziers.
        const rx = parseFloat(tokens[i]);
        const ry = parseFloat(tokens[i + 1]);
        const xRot = parseFloat(tokens[i + 2]) * Math.PI / 180;
        const largeArc = tokens[i + 3] === '1';
        const sweep = tokens[i + 4] === '1';
        const ex = parseFloat(tokens[i + 5]);
        const ey = flipY(parseFloat(tokens[i + 6]));
        const beziers = arcToBeziers(cx, cy, ex, ey, rx, ry, xRot, largeArc, sweep);
        for (const b of beziers) {
          ops.push(`${b.c1x} ${b.c1y} ${b.c2x} ${b.c2y} ${b.ex} ${b.ey} c`);
        }
        cx = ex; cy = ey;
        i += 7;
        break;
      }
      default:
        // Unknown command; skip the token rather than crash.
        i++;
    }
  }
  return ops;
}

interface Bezier { c1x: number; c1y: number; c2x: number; c2y: number; ex: number; ey: number; }

/**
 * Elliptical arc → cubic Beziers. Standard SVG-spec algorithm; emits ≤ 4
 * cubics per 360°. Reference: https://www.w3.org/TR/SVG/implnote.html#ArcImplementationNotes
 */
function arcToBeziers(
  x1: number, y1: number, x2: number, y2: number,
  rx: number, ry: number, xRot: number,
  largeArc: boolean, sweep: boolean,
): Bezier[] {
  if (rx === 0 || ry === 0) return [{ c1x: x1, c1y: y1, c2x: x2, c2y: y2, ex: x2, ey: y2 }];
  rx = Math.abs(rx); ry = Math.abs(ry);

  const cosR = Math.cos(xRot), sinR = Math.sin(xRot);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p =  cosR * dx + sinR * dy;
  const y1p = -sinR * dx + cosR * dy;

  // Correct out-of-range radii
  const lam = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lam > 1) { const s = Math.sqrt(lam); rx *= s; ry *= s; }

  const sign = largeArc === sweep ? -1 : 1;
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const k = Math.sqrt(Math.max(0, num / den));
  const cxp =  sign * k * (rx * y1p) / ry;
  const cyp = -sign * k * (ry * x1p) / rx;
  const cx = cosR * cxp - sinR * cyp + (x1 + x2) / 2;
  const cy = sinR * cxp + cosR * cyp + (y1 + y2) / 2;

  const ang = (ux: number, uy: number, vx: number, vy: number) => {
    const dot = ux * vx + uy * vy;
    const mag = Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy));
    const cos = Math.max(-1, Math.min(1, dot / (mag || 1)));
    const a = Math.acos(cos);
    return (ux * vy - uy * vx) < 0 ? -a : a;
  };
  const theta1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dTheta = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dTheta > 0) dTheta -= 2 * Math.PI;
  if ( sweep && dTheta < 0) dTheta += 2 * Math.PI;

  // Split into ≤ 4 segments so each cubic covers ≤ 90°.
  const nSeg = Math.max(1, Math.ceil(Math.abs(dTheta) / (Math.PI / 2)));
  const segDt = dTheta / nSeg;
  const t = (4 / 3) * Math.tan(segDt / 4);

  const beziers: Bezier[] = [];
  for (let s = 0; s < nSeg; s++) {
    const a0 = theta1 + s * segDt;
    const a1 = theta1 + (s + 1) * segDt;
    const cos0 = Math.cos(a0), sin0 = Math.sin(a0);
    const cos1 = Math.cos(a1), sin1 = Math.sin(a1);

    const p0x = cx + cosR * rx * cos0 - sinR * ry * sin0;
    const p0y = cy + sinR * rx * cos0 + cosR * ry * sin0;
    const p3x = cx + cosR * rx * cos1 - sinR * ry * sin1;
    const p3y = cy + sinR * rx * cos1 + cosR * ry * sin1;

    const c1x = p0x + t * (-cosR * rx * sin0 - sinR * ry * cos0);
    const c1y = p0y + t * (-sinR * rx * sin0 + cosR * ry * cos0);
    const c2x = p3x - t * (-cosR * rx * sin1 - sinR * ry * cos1);
    const c2y = p3y - t * (-sinR * rx * sin1 + cosR * ry * cos1);
    beziers.push({ c1x, c1y, c2x, c2y, ex: p3x, ey: p3y });
  }
  return beziers;
}
